import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import './Admin.css';

const CATEGORIES = ['espresso', 'filter', 'capsule', 'cold-brew', 'accessories', 'merchandise', 'gift-sets'];
const ROAST_LEVELS = ['light', 'medium', 'medium-dark', 'dark', 'extra-dark'];

const EMPTY_PRODUCT = { name: '', description: '', shortDescription: '', price: '', category: 'espresso', origin: '', roastLevel: 'medium', stock: 0, isFeatured: false, isActive: true, thumbnail: '', weight: { value: 250, unit: 'g' }, flavor: '' };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/products?search=${search}&limit=50`);
      setProducts(res.data.products);
    } catch (err) { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, [search]);

  const openAdd = () => { setForm(EMPTY_PRODUCT); setModal('add'); };
  const openEdit = (p) => {
    setForm({ ...p, flavor: p.flavor?.join(', ') || '', weight: p.weight || { value: 250, unit: 'g' } });
    setModal('edit');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price), flavor: form.flavor ? form.flavor.split(',').map(s => s.trim()) : [] };
      if (modal === 'add') {
        const res = await api.post('/admin/products', data);
        setProducts(prev => [res.data.product, ...prev]);
        toast.success('Product created!');
      } else {
        const res = await api.put(`/admin/products/${form._id}`, data);
        setProducts(prev => prev.map(p => p._id === form._id ? res.data.product : p));
        toast.success('Product updated!');
      }
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"?`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts(prev => prev.filter(p => p._id !== id));
      toast.success('Product deactivated');
    } catch (err) { toast.error('Delete failed'); }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Products</h1>
        <button className="btn-primary" onClick={openAdd}><Plus size={16} /> Add Product</button>
      </div>
      <div className="admin-card">
        <div className="admin-toolbar">
          <div className="admin-search"><Search size={16} /><input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        </div>
        {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div> : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {products.map(p => (
                  <tr key={p._id}>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <img src={p.thumbnail || 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=60'} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8 }} />
                      <div><strong style={{ display: 'block', color: 'var(--gray-800)' }}>{p.name}</strong><span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{p.origin}</span></div>
                    </div></td>
                    <td><span style={{ textTransform: 'capitalize', fontSize: 13 }}>{p.category}</span></td>
                    <td><strong>CHF {p.price?.toFixed(2)}</strong></td>
                    <td><span style={{ color: p.stock < 10 ? 'var(--error)' : 'var(--success)', fontWeight: 700 }}>{p.stock}</span></td>
                    <td><span style={{ background: p.isActive ? '#10b98122' : '#ef444422', color: p.isActive ? '#10b981' : '#ef4444', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{p.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={() => openEdit(p)}><Edit2 size={14} /></button>
                        <button style={{ padding: '6px 12px', fontSize: 13, background: '#ef444411', border: '1px solid #ef4444', borderRadius: 6, color: '#ef4444', cursor: 'pointer' }} onClick={() => handleDelete(p._id, p.name)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{modal === 'add' ? 'Add Product' : 'Edit Product'}</h3>
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group"><label>Name</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
                <div className="form-group"><label>Price (CHF)</label><input type="number" step="0.1" className="form-control" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required /></div>
                <div className="form-group"><label>Category</label><select className="form-control" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div className="form-group"><label>Roast Level</label><select className="form-control" value={form.roastLevel} onChange={e => setForm({ ...form, roastLevel: e.target.value })}>{ROAST_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                <div className="form-group"><label>Stock</label><input type="number" className="form-control" value={form.stock} onChange={e => setForm({ ...form, stock: parseInt(e.target.value) })} required /></div>
                <div className="form-group"><label>Origin</label><input className="form-control" value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Short Description</label><input className="form-control" value={form.shortDescription} onChange={e => setForm({ ...form, shortDescription: e.target.value })} /></div>
              <div className="form-group"><label>Description</label><textarea className="form-control" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></div>
              <div className="form-group"><label>Thumbnail URL</label><input className="form-control" value={form.thumbnail} onChange={e => setForm({ ...form, thumbnail: e.target.value })} /></div>
              <div className="form-group"><label>Flavor Notes (comma-separated)</label><input className="form-control" value={form.flavor} onChange={e => setForm({ ...form, flavor: e.target.value })} placeholder="chocolate, caramel, berry" /></div>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}><input type="checkbox" checked={form.isFeatured} onChange={e => setForm({ ...form, isFeatured: e.target.checked })} /> Featured</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
